import { memo, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { PanelLeftClose, PanelLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { SidebarSection } from "./SidebarSection";
import { SidebarItem } from "./SidebarItem";
import { mainNavSections, utilityNavItems } from "./sidebarData";
import { useNavPermissions } from "./useNavPermissions";
import { useAiAssistantEnabled } from "@/features/settings/hooks/useSettings";
import IconButton from "@/components/common/GoogleIconButton";

export const Sidebar = memo(function Sidebar() {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const { isBelow } = useBreakpoint();
  const { canViewItem, homePath } = useNavPermissions();
  const aiAssistantEnabled = useAiAssistantEnabled();
  const location = useLocation();

  const visibleUtilityItems = useMemo(
    () =>
      utilityNavItems.filter(
        (item) =>
          canViewItem(item) && (aiAssistantEnabled || item.path !== "/chat"),
      ),
    [canViewItem, aiAssistantEnabled],
  );

  if (isBelow("lg")) return null;

  return (
    <aside
      className={cn(
        "flex h-full shrink-0 flex-col bg-sidebar relative",
        sidebarCollapsed ? "w-[68px]" : "w-[260px]",
      )}
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-primary/[0.02] via-transparent to-transparent dark:from-primary/[0.03]" />

      {/* Logo area */}
      <div
        className={cn(
          "relative flex items-center border-b border-sidebar-border/40",
          sidebarCollapsed
            ? "flex-col gap-1.5 px-2 py-4"
            : "h-[60px] gap-3 px-5",
        )}
      >
        <Link
          to={homePath}
          aria-label={homePath === "/home" ? "Go to home" : "Go to customers"}
          className="group relative rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary focus-visible:shadow-none"
        >
          <div className="absolute inset-0 rounded-xl bg-primary/15 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <img
            src={sidebarCollapsed ? "favicon.png" : "/planetwise-logo.png"}
            alt="Planetwise"
            className={cn(
              "relative shrink-0",
              sidebarCollapsed ? "h-8 w-8" : "w-25",
            )}
          />
        </Link>
        {/* {!sidebarCollapsed && (

        )} */}
        <IconButton
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!sidebarCollapsed}
          className={sidebarCollapsed ? "" : "ml-auto"}
          sx={{
            padding: "0.375rem",
            borderRadius: "0.5rem",
            color: "var(--sidebar-foreground-25)",
            "&:hover": {
              backgroundColor: "var(--sidebar-accent-60)",
              color: "var(--sidebar-foreground-60)",
            },
          }}
        >
          {sidebarCollapsed ? (
            <PanelLeft size={15} />
          ) : (
            <PanelLeftClose size={15} />
          )}
        </IconButton>
      </div>

      {/* Main navigation */}
      <nav
        aria-label="Main navigation"
        className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 py-3 space-y-4 scrollbar-none"
      >
        {mainNavSections.map((section) => (
          <SidebarSection
            key={section.title ?? section.items[0]?.path}
            section={section}
            collapsed={sidebarCollapsed}
          />
        ))}
      </nav>

      {/* Utility items - pinned to bottom */}
      <div className="relative border-t border-sidebar-border/40 px-2.5 py-3 space-y-0.5">
        <div className="absolute -top-px left-4 right-4 h-px bg-gradient-to-r from-transparent via-primary/15 to-transparent" />
        {visibleUtilityItems.map((item) => (
          <SidebarItem
            key={item.path}
            item={item}
            collapsed={sidebarCollapsed}
            isActive={
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/")
            }
          />
        ))}
      </div>
    </aside>
  );
});
